<template>
  <div class="login-page">
    <div class="login-card">
      <div class="login-brand">
        <div v-if="brandingLogoUrl" class="login-logo-wrap">
          <img :src="brandingLogoUrl" alt="Logo" class="login-logo-img" />
        </div>
        <div v-else class="login-avatar">
          <v-icon size="32" color="#fff">mdi-shield-account</v-icon>
        </div>
        <div class="login-brand-text">{{ brandingName || 'BTP' }}</div>
        <div class="login-brand-sub">Panel administracyjny</div>
      </div>
      <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="login">
        <v-text-field
          v-model="username"
          name="login"
          label="E-mail"
          :rules="emailRules"
          type="text"
          outlined dense
          prepend-inner-icon="mdi-email"
        />
        <v-text-field
          id="password"
          v-model="password"
          name="password"
          :label="$t('login.password')"
          :type="showPwd ? 'text' : 'password'"
          :rules="passwordRules"
          outlined dense
          prepend-inner-icon="mdi-lock"
          :append-icon="showPwd ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append="showPwd = !showPwd"
          @keyup.enter="login()"
        />
        <button
          type="submit"
          class="login-submit"
          :disabled="!valid"
          @click.prevent="login()"
        >
          <v-icon size="16">mdi-login</v-icon>
          {{ $t('login.loginButton') }}
        </button>
      </v-form>
      <button type="button" class="login-forgot" @click="forgotPasswordEmail">
        <v-icon size="14">mdi-email-lock</v-icon>
        Nie pamiętam hasła — wyślij kod na e-mail
      </button>
      <button type="button" class="login-forgot login-forgot-alt" @click="forgotPasswordSms">
        <v-icon size="14">mdi-cellphone-message</v-icon>
        Albo hasło tymczasowe SMS-em
      </button>
    </div>
  </div>
</template>

<script>
const axios = require("axios");
import Swal from "sweetalert2";

export default {
  name: "LoginPage",
  data: () => ({
    valid: true,
    password: "",
    username: "",
    showPwd: false,
    brandingLogoUrl: null,
    brandingName: null,
    emailRules: [
      (v) => !!v || "Podaj Poprawny Adres E-mail",
      (v) => /.+@.+\..+/.test(v) || "Podaj Poprawny Adres E-mail",
    ],
    passwordRules: [(v) => !!v || "Hasło Jest Wymagane"],
  }),
  props: {
    source: String,
  },
  async created() {
    try {
      const res = await axios({
        method: 'get',
        url: process.env.VUE_APP_ROOT_API + 'company/public/branding',
      });
      const d = res.data && res.data.data;
      if (d) {
        this.brandingLogoUrl = d.logoUrl || null;
        this.brandingName = d.name || null;
      }
    } catch (e) {
      // best-effort; falls back to default avatar + "BTP"
    }
  },
  methods: {
    showLoginError(title, text) {
      Swal.fire({
        icon: "error",
        title,
        text,
        background: "#232838",
        color: "#E6EAF2",
        iconColor: "#FF8A80",
        confirmButtonText: this.$t("login.ok"),
        confirmButtonColor: "#2962FF",
      });
    },
    // Reset hasła e-mailem: kod z wiadomości + nowe hasło. Dopóki admin nie
    // dokończy procedury, dotychczasowe hasło działa — awaria wysyłki nikogo
    // nie odcina od panelu (inaczej niż przy haśle tymczasowym SMS-em).
    async forgotPasswordEmail() {
      const { value: email } = await Swal.fire({
        title: "Nie pamiętam hasła",
        text: "Podaj adres e-mail swojego konta — wyślemy na niego kod weryfikacyjny.",
        input: "email",
        inputValue: this.username ? this.username.trim() : "",
        inputPlaceholder: "adres@btpartner.pl",
        background: "#232838",
        color: "#E6EAF2",
        showCancelButton: true,
        confirmButtonText: "Wyślij kod",
        cancelButtonText: "Anuluj",
        confirmButtonColor: "#2962FF",
        inputValidator: (v) =>
          !v || !/.+@.+\..+/.test(v.trim()) ? "Podaj poprawny adres e-mail" : undefined,
      });
      if (!email) return;
      const cleanEmail = email.trim().toLowerCase();

      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "admin-auth/forgot-password-email",
          headers: { "Content-Type": "application/json" },
          data: { email: cleanEmail },
        });
      } catch (e) {
        const status = e.response && e.response.status;
        this.showLoginError(
          "Błąd",
          status === 429
            ? "Za dużo prób. Spróbuj ponownie za chwilę."
            : (e.response && e.response.data && e.response.data.message) ||
              "Nie udało się wysłać e-maila. Spróbuj ponownie."
        );
        return;
      }

      // Krok 2: kod + nowe hasło. Pętla, żeby literówka w kodzie nie zaczynała
      // całej procedury od nowa (kod jest ważny 15 minut).
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value: form, isDismissed } = await Swal.fire({
          title: "Ustaw nowe hasło",
          text: `Kod wysłaliśmy na ${cleanEmail}. Jest ważny 15 minut.`,
          background: "#232838",
          color: "#E6EAF2",
          html:
            '<input id="rc" class="swal2-input" placeholder="Kod z e-maila" autocomplete="one-time-code">' +
            '<input id="rp1" type="password" class="swal2-input" placeholder="Nowe hasło">' +
            '<input id="rp2" type="password" class="swal2-input" placeholder="Powtórz hasło">',
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: "Zapisz hasło",
          cancelButtonText: "Anuluj",
          confirmButtonColor: "#2962FF",
          preConfirm: () => {
            const code = document.getElementById("rc").value;
            const p1 = document.getElementById("rp1").value;
            const p2 = document.getElementById("rp2").value;
            if (!code || !code.trim()) {
              Swal.showValidationMessage("Podaj kod z e-maila");
              return false;
            }
            if (!p1 || p1.length < 8) {
              Swal.showValidationMessage("Hasło musi mieć min. 8 znaków");
              return false;
            }
            if (p1 !== p2) {
              Swal.showValidationMessage("Hasła nie są takie same");
              return false;
            }
            return { code: code.trim(), newPassword: p1 };
          },
        });
        if (isDismissed || !form) return;

        try {
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "admin-auth/reset-password",
            headers: { "Content-Type": "application/json" },
            data: { email: cleanEmail, code: form.code, newPassword: form.newPassword },
          });
          await Swal.fire({
            icon: "success",
            title: "Hasło zmienione",
            text: "Możesz zalogować się nowym hasłem.",
            background: "#232838",
            color: "#E6EAF2",
            confirmButtonColor: "#2962FF",
          });
          this.username = cleanEmail;
          this.password = "";
          return;
        } catch (e) {
          const msg =
            (e.response && e.response.data && e.response.data.message) ||
            "Nie udało się zmienić hasła. Spróbuj ponownie.";
          this.showLoginError("Błąd", msg);
        }
      }
    },
    async forgotPasswordSms() {
      const { value: phone } = await Swal.fire({
        title: "Przypomnienie hasła (SMS)",
        text: "Podaj numer telefonu przypisany do konta administratora — wyślemy SMS z hasłem tymczasowym.",
        input: "tel",
        inputPlaceholder: "np. 600 123 456",
        background: "#232838",
        color: "#E6EAF2",
        showCancelButton: true,
        confirmButtonText: "Wyślij SMS",
        cancelButtonText: "Anuluj",
        confirmButtonColor: "#2962FF",
        inputValidator: (v) => (!v || !v.trim() ? "Podaj numer telefonu" : undefined),
      });
      if (!phone) return;
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "admin-auth/forgot-password-sms",
          headers: { "Content-Type": "application/json" },
          data: { phone: phone.trim() },
        });
        const msg = (res.data && res.data.data && res.data.data.message) ||
          "Jeśli numer jest przypisany do konta, wyślemy SMS z hasłem tymczasowym.";
        Swal.fire({
          icon: "success",
          title: "Wysłano",
          text: msg,
          background: "#232838",
          color: "#E6EAF2",
          confirmButtonColor: "#2962FF",
        });
      } catch (e) {
        const status = e.response && e.response.status;
        const text = status === 429
          ? "Za dużo prób. Spróbuj ponownie za chwilę."
          : (e.response && e.response.data && e.response.data.message) ||
            "Nie udało się wysłać SMS. Spróbuj ponownie.";
        this.showLoginError("Błąd", text);
      }
    },
    // Wymuszona zmiana hasła po zalogowaniu hasłem tymczasowym. Zwraca true gdy
    // hasło zostało zmienione, false gdy admin zrezygnował (anulujemy logowanie).
    async forceChangePassword(token) {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value: newPassword, isDismissed } = await Swal.fire({
          title: "Ustaw nowe hasło",
          text: "Logujesz się hasłem tymczasowym — ustaw teraz własne hasło (min. 8 znaków).",
          background: "#232838",
          color: "#E6EAF2",
          html:
            '<input id="np1" type="password" class="swal2-input" placeholder="Nowe hasło">' +
            '<input id="np2" type="password" class="swal2-input" placeholder="Powtórz hasło">',
          focusConfirm: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: true,
          confirmButtonText: "Zapisz hasło",
          cancelButtonText: "Wyloguj",
          confirmButtonColor: "#2962FF",
          preConfirm: () => {
            const p1 = document.getElementById("np1").value;
            const p2 = document.getElementById("np2").value;
            if (!p1 || p1.length < 8) {
              Swal.showValidationMessage("Hasło musi mieć min. 8 znaków");
              return false;
            }
            if (p1 !== p2) {
              Swal.showValidationMessage("Hasła nie są takie same");
              return false;
            }
            return p1;
          },
        });
        if (isDismissed) return false;
        try {
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "admin-auth/change-password",
            headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
            data: { newPassword },
          });
          return true;
        } catch (e) {
          // Pokazujemy komunikat z backendu — generyczne „spróbuj ponownie"
          // zapętlało dialog bez żadnej wskazówki, co jest nie tak.
          const msg =
            (e.response && e.response.data && e.response.data.message) ||
            "Nie udało się zmienić hasła. Spróbuj ponownie.";
          this.showLoginError("Błąd", msg);
        }
      }
    },
    async login() {
      try {
        if (!this.username.trim().length) {
          this.showLoginError(this.$t("login.failedTitle"), this.$t("login.enterEmail"));
          return;
        }
        if (!this.password.trim().length) {
          this.showLoginError(this.$t("login.failedTitle"), this.$t("login.enterPassword"));
          return;
        }
        const user = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "admin-auth/login",
          headers: {
            "Content-Type": "application/json",
          },
          data: {
            email: this.username.trim(),
            password: this.password.trim(),
          },
        });
        localStorage.setItem("user", true);
        localStorage.setItem("token", user.data.access_token);
        const pages = Array.isArray(user.data.pages) ? user.data.pages : [];
        localStorage.setItem("pages", JSON.stringify(pages));
        const pagesEdit = Array.isArray(user.data.pagesWithEdit) ? user.data.pagesWithEdit : [];
        localStorage.setItem("pagesWithEdit", JSON.stringify(pagesEdit));
        localStorage.setItem("role", user.data.role);
        localStorage.setItem("actualRole", user.data.actualRole || user.data.role || '');
        localStorage.setItem("isHead", String(!!user.data.isHead));
        // Pin the active company to the admin's main companyId from JWT;
        // otherwise frontend would default to first-in-list and queries
        // would silently leak data from the JWT-bound company.
        if (user.data.companyId) {
          localStorage.setItem("activeCompanyId", user.data.companyId);
        }
        if (user.data.companyName) {
          localStorage.setItem("activeCompanyName", user.data.companyName);
        }
        // Logowanie hasłem tymczasowym (SMS) — wymuś zmianę hasła zanim wpuścimy.
        if (user.data.mustChangePassword) {
          const changed = await this.forceChangePassword(user.data.access_token);
          if (!changed) {
            // Admin zrezygnował — anuluj logowanie, wymuś ponowne wejście.
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            localStorage.removeItem("pages");
            localStorage.removeItem("pagesWithEdit");
            localStorage.removeItem("role");
            return;
          }
        }
        // Konta z ograniczonymi uprawnieniami (user/office) lądują na stronie,
        // do której NA PEWNO mają dostęp — dashboard jeśli wolno, inaczej
        // pierwsza dozwolona. Wcześniej 'office' szedł na "/" (Administratorzy)
        // i dostawał 403, a menu pokazywało wszystko.
        const privileged = user.data.role === "superadmin" || user.data.role === "headadmin";
        if (!privileged && pages.length > 0) {
          const landing = pages.includes("dashboard") ? "dashboard" : pages[0];
          window.location.href = "/" + (landing || "dashboard");
        } else {
          window.location.href = "/";
        }
      } catch (e) {
        console.log(e);
        const status = e.response?.status;
        if (status === 401 || status === 403 || status === 404) {
          this.showLoginError(this.$t("login.failedTitle"), this.$t("login.invalidCredentials"));
        } else {
          this.showLoginError(this.$t("login.failedTitle"), this.$t("login.genericError"));
        }
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("pages");
        localStorage.removeItem("pagesWithEdit");
        localStorage.removeItem("role");
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  background: #181C28;
  min-height: 100vh;
  margin: -24px;
  padding: 32px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.login-card {
  width: 100%;
  max-width: 420px;
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 22px;
  padding: 32px 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}
.login-brand {
  text-align: center;
  margin-bottom: 24px;
}
.login-avatar {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(41, 98, 255, 0.4);
  margin-bottom: 14px;
}
.login-logo-wrap {
  width: 140px;
  height: 140px;
  margin: 0 auto 18px;
  border-radius: 24px;
  background: linear-gradient(135deg, #1F2434 0%, #232838 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
}
.login-logo-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}
.login-brand-text {
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
}
.login-brand-sub {
  font-size: 13px;
  color: #6E7691;
  font-weight: 500;
  margin-top: 2px;
}
.login-submit {
  width: 100%;
  margin-top: 12px;
  padding: 13px 16px;
  background: #2962FF;
  border: none;
  border-radius: 12px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 8px 18px rgba(41, 98, 255, 0.4);
  transition: transform 0.12s ease, box-shadow 0.15s ease;
  font-family: inherit;
}
.login-forgot {
  width: 100%;
  margin-top: 14px;
  background: none;
  border: none;
  color: #8FA6E0;
  font-size: 12.5px;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: color 0.12s ease;
}
.login-forgot:hover {
  color: #B9C9F4;
  text-decoration: underline;
}
/* Druga, mniej wyeksponowana ścieżka odzyskiwania (SMS). */
.login-forgot-alt {
  color: #6B7A9E;
  font-size: 11.5px;
  margin-top: 2px;
}
.login-forgot-alt:hover {
  color: #8FA6E0;
}
.login-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(41, 98, 255, 0.5);
}
.login-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.login-submit .v-icon { color: #fff !important; }

/* Inputs in dark theme */
.login-page >>> .v-text-field--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.login-page >>> .v-text-field--outlined .v-input__slot {
  background: #181C28 !important;
  border-radius: 12px !important;
  min-height: 44px !important;
}
.login-page >>> .v-label { color: #6E7691 !important; font-size: 13px !important; }
.login-page >>> .v-text-field input { color: #fff !important; }
.login-page >>> .v-input__prepend-inner .v-icon,
.login-page >>> .v-input__append-inner .v-icon { color: #6E7691 !important; }
.login-page >>> .v-text-field--outlined.v-input--is-focused fieldset { border-color: #5478FF !important; }
.login-page >>> .v-messages__message { color: #FF8A80 !important; font-size: 11px; }
</style>
